import React from "react"
import { API, graphqlOperation } from "aws-amplify"
import { useSnackbar } from "notistack"
import { Link as RouterLink, useParams } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import { useRecoilState } from "recoil"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import useMediaQuery from "@material-ui/core/useMediaQuery"
import CircularProgress from "@material-ui/core/CircularProgress"
import Grid from "@material-ui/core/Grid"
import Typography from "@material-ui/core/Typography"
import EditIcon from "@material-ui/icons/Edit"
import PrintIcon from "@material-ui/icons/Print"

import DisclosureHeader from "./DisclosuresHeader"
import Fab from "../../components/Fab"
import Layout from "../../components/AuthenticatedLayout"
import DocumentContainer from "../../components/DocumentContainer"
import FormContainer from "../../components/FormContainer"
import { getDisclosure } from "../../graphqlCustom/queries"
import { formatDateLong as formatDate, formatMoney } from "../../libs/format"
import { fetchAssociation } from "../../libs/manageAccounts"
import currentAssociation from "../../store/currentAssociation"

const useStyles = makeStyles((theme) => ({
  answer: {
    fontWeight: 700,
  },
  break: {
    marginBottom: theme.spacing(3),
  },
  label: {
    fontWeight: 400,
  },
  number: {
    fontSize: "1.15em",
    fontWeight: 700,
    width: theme.spacing(2),
  },
}))

const Answer = ({ children }) => {
  const classes = useStyles()
  return (
    <Typography variant="body1" className={classes.answer} display="inline">
      {children}
    </Typography>
  )
}

const Label = ({ children }) => {
  const classes = useStyles()
  return (
    <Typography variant="body1" className={classes.label} display="inline">
      {children}
    </Typography>
  )
}

const Number = ({ children }) => {
  const classes = useStyles()
  return (
    <Typography variant="body1" className={classes.number} display="inline">
      {children}
    </Typography>
  )
}

export default function Disclosures() {
  const { disclosure_id: itemId } = useParams()
  const [association, setAssociation] = useRecoilState(currentAssociation)
  const { enqueueSnackbar } = useSnackbar()
  const [item, setItem] = React.useState({})
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const printRef = React.useRef()
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  })

  React.useEffect(() => {
    try {
      if (association.id) {
        fetchAssociation(association.id)
          .then(({ data }) => {
            console.log("association data", data)
            if (data && data.getAssociation) {
              setAssociation(data.getAssociation)
            }
          })
          .catch((error) => console.log("error", error))
      }
    } catch (error) {
      console.log("error", error)
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    console.log("association", association)
    async function fetchItem() {
      try {
        const { data } = await API.graphql(
          graphqlOperation(getDisclosure, { id: itemId, associationId: association.id })
        )
        if (data && data.getDisclosure) {
          setItem(data.getDisclosure)
        }
      } catch (error) {
        console.log("error", error)
        enqueueSnackbar("Error fetching disclosure", {
          variant: "error",
        })
      }
    }
    if (association.id) {
      fetchItem()
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout title="Sale Disclosures">
      <DisclosureHeader />
      <FormContainer fullWidth transparent wideContent>
        <Grid container spacing={2} justifyContent={isMobile ? "center" : "space-between"}>
          <Grid item xs={6} md={8}>
            <Typography variant="h1">Disclosure Document</Typography>
          </Grid>
          <Grid
            item
            xs={6}
            md={4}
            container
            alignItems="center"
            justifyContent="flex-end"
            style={{ marginRight: -12, marginTop: -12, textAlign: "right" }}
          >
            <Fab onClick={handlePrint}>
              <PrintIcon />
            </Fab>

            <Fab component={RouterLink} edge="end" to={`/0/sale/${itemId}/edit`} variant="extended">
              <EditIcon />
              Edit
            </Fab>
          </Grid>
        </Grid>
      </FormContainer>
      <FormContainer fullWidth transparent wideContent>
        {item ? (
          <DocumentContainer ref={printRef}>
            <Grid container spacing={2}>
              <Grid container justifyContent="center" item xs={12}>
                <Typography
                  variant="h1"
                  className={classes.break}
                  style={{ textTransform: "uppercase" }}
                >
                  Condominium Disclosure Statement
                </Typography>
              </Grid>
            </Grid>
            <Grid container justifyContent="center" spacing={1}>
              <Grid item>
                <Label>Property Address:</Label>
              </Grid>
              <Grid item>
                <Answer>{association.address}</Answer>
              </Grid>
              <Grid item>
                <Label>Unit #:</Label>
              </Grid>
              <Grid item>
                <Answer>{item.unit}</Answer>
              </Grid>
              <Grid item>
                <Label>City:</Label>
              </Grid>
              <Grid item>
                <Answer>
                  {association.city}
                  {association.state ? `, ${association.state}` : ""}
                </Answer>
              </Grid>
              <Grid item>
                <Label>Postal Code:</Label>
              </Grid>
              <Grid item>
                <Answer>{association.postalCode}</Answer>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  className={classes.break}
                  style={{ padding: "0 16px", textAlign: "center" }}
                >
                  The following statements are provided by the Board of Managers of the{" "}
                  {association.name} Conominium Assocation as required by 765 ILCS 605/22.1 of the
                  Illinois Condominium Property Act:
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <Number>1.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>Association Name:</Label> <Answer>{association.name}</Answer>
              </Grid>
              <Grid item xs={1}>
                <Number>2.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>Are there any liens against the Association?</Label>{" "}
                <Answer>{item.haveLiens ? "Yes" : "No"}</Answer>
                <br />
                {item.haveLiens ? (
                  <>
                    <Label>Details:</Label> <Answer>{item.liensDescription}</Answer>
                  </>
                ) : (
                  ""
                )}
              </Grid>
              <Grid item xs={1}>
                <Number>3.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>Amount of Reserves for capital expenditures:</Label>{" "}
                <Answer>{formatMoney(item.reservesAmount)}</Answer>
              </Grid>
              <Grid item xs={1}>
                <Number>4.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>
                  Are there any capital expenditures anticipated by the Association for the current
                  or next two (2) fiscal years which would require a special assessment or increase
                  in the monthly assessment to the unit owners?
                </Label>{" "}
                <Answer>{item.haveCapitalExpenditures ? "Yes" : "No"}</Answer>
                <br />
                {item.haveCapitalExpenditures ? (
                  <>
                    <Label>Type:</Label>{" "}
                    <Answer>
                      {item.capitalExpendituresType}
                      {item.capitalExpendituresType === "Other"
                        ? `, ${item.capitalExpendituresOther}`
                        : ""}
                    </Answer>
                    <br />
                    {item.haveSpecialAssessment ? (
                      <>
                        <Label>Special Assessment Amount:</Label>{" "}
                        <Answer>{formatMoney(item.specialAssessmentAmount)}</Answer>
                        <br />
                      </>
                    ) : (
                      ""
                    )}
                    {item.haveIncreaseMonthly ? (
                      <>
                        <Label>Increased monthly assessment:</Label>{" "}
                        <Answer>
                          {formatMoney(item.increaseMonthlyAmount)}, end date:{" "}
                          {formatDate(item.increaseMonthlyEndDate)}
                        </Answer>
                        <br />
                      </>
                    ) : (
                      ""
                    )}
                  </>
                ) : (
                  ""
                )}
              </Grid>
              <Grid item xs={1}>
                <Number>5.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>
                  Any improvements or alterations in the above referenced unit, or in the limited
                  common elements assigned to the unit by the current and any prior owners, are in
                  good faith believed to be in compliance with the Condominium Declaration.
                </Label>{" "}
                <Answer>{item.haveCompliantAlterations ? "Yes" : "No"}</Answer>
                <br />
                {!item.haveCompliantAlterations ? (
                  <>
                    <Label>Details:</Label> <Answer>{item.compliantAlterationsDescription}</Answer>
                  </>
                ) : (
                  ""
                )}
              </Grid>
              <Grid item xs={1}>
                <Number>6.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>Are there pending lawsuits or judgments against the Association?</Label>{" "}
                <Answer>{item.haveLawsuits ? "Yes" : "No"}</Answer>
                <br />
                {item.haveLawsuits ? (
                  <>
                    <Label>Details:</Label> <Answer>{item.lawsuitsDescription}</Answer>
                  </>
                ) : (
                  ""
                )}
              </Grid>
              <Grid item xs={1}>
                <Number>7.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>A copy of the Association's latest financial statement:</Label>{" "}
                <Answer>{item.disclosureLatestFinancialsId ? "Attached" : "Not attached"}</Answer>
                <br />
                <br />
                <Label>A copy of the Association's latest operating budget:</Label>{" "}
                <Answer>{item.disclosureLatestBudgetId ? "Attached" : "Not attached"}</Answer>
              </Grid>
              <Grid item xs={1}>
                <Number>8.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>
                  A copy of the board minutes showing capital expenditures and/or approval of
                  special assessments:
                </Label>{" "}
                <Answer>{item.disclosureLatestMinutesId ? "Attached" : "Not attached"}</Answer>
              </Grid>
              <Grid item xs={1}>
                <Number>9.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>
                  The following insurance coverage is provided by the Association for all Unit
                  Owners.
                </Label>
                <br />
                <Label>Company:</Label>
                <br />
                <Answer>
                  {item.insuranceName}
                  <br />
                  {item.insuranceAddress}
                  <br />
                </Answer>
                <Label>Contact:</Label>
                <br />
                <Answer>
                  {item.insuranceContactName}
                  <br />
                  {item.insurancePhone}
                  <br />
                  {item.insuranceEmail}
                  <br />
                </Answer>
              </Grid>
              <Grid item xs={1}>
                <Number>10.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>Current monthly assessment for this unit:</Label>{" "}
                <Answer>{formatMoney(item.asssessmentAmount)}</Answer>
              </Grid>
              <Grid item xs={1}>
                <Number>11.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>Amount of unpaid assessments or other charges due and owing:</Label>{" "}
                <Answer>{formatMoney(item.unpaidAssessmentAmount)}</Answer>
              </Grid>
              <Grid item xs={1}>
                <Number>12.</Number>
              </Grid>
              <Grid item xs={11}>
                <Label>All Notices regarding the Association shall be directed to:</Label>
                <br />
                <Answer>{item.contactEmail}</Answer>
                <br />
                <Typography variant="body1">
                  The Board of Managers of the {association.name} Condominium Association
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={5} style={{ marginTop: theme.spacing(1) }}>
              <Grid item xs={12}>
                <Label>Prepared by </Label> <Answer>{item.preparedByName}</Answer> on{" "}
                <Answer>{formatDate(item.preparedDate)}</Answer>
                <Typography variant="body2">
                  Authorized Agent of the Association/Board of Managers
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={5} style={{ marginTop: theme.spacing(4) }}>
              <Grid item xs={12} style={{ pageBreakBefore: "always" }}>
                <Typography variant="body2">
                  (765 ILCS 605/22.1) (from Ch. 30, par. 322.1)
                </Typography>
                <Typography variant="body2">
                  Sec. 22.1. (a) In the event of any resale of a condominium unit by a unit owner
                  other than the developer such owner shall obtain from the Board of Managers and
                  shall make available for inspection to the prospective purchaser, upon demand, the
                  following:
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: theme.spacing(2) }}>
                  (1) A copy of the Declaration, by-laws, other condominium instruments and any
                  rules and regulations.
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: theme.spacing(2) }}>
                  (2) A statement of any liens, including a statement of the account of the unit
                  setting forth the amounts of unpaid assessments and other charges due and owing as
                  authorized and limited by the provisions of Section 9 of this Act or the
                  condominium instruments. (3) A statement of any capital expenditures anticipated
                  by the unit owner's association within the current or succeeding two fiscal years.
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: theme.spacing(2) }}>
                  (4) A statement of the status and amount of any reserve for replacement fund and
                  any portion of such fund earmarked for any speci fied project by the Board of
                  Managers.
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: theme.spacing(2) }}>
                  (5) A copy of the statement of financial condition of the unit owner's association
                  for the last fiscal year for which such statement is available.
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: theme.spacing(2) }}>
                  (6) A statement of the status of any pending suits or judgments in which the unit
                  owner's association is a party.
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: theme.spacing(2) }}>
                  (7) A statement setting forth what insurance coverage is provided for all unit
                  owners by the unit owner's association.
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: theme.spacing(2) }}>
                  (8) A statement that any improvements or alterations made to the unit, or the
                  limited common elements assigned thereto, by the prior unit owner are in good
                  faith believed to be in compliance with the condominium instruments.
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: theme.spacing(2) }}>
                  (9) The identity and mailing address of the principal officer of the unit owner's
                  association or of the other officer or agent as is specifically designated to
                  receive notices.
                </Typography>
                <Typography variant="body2">
                  (b) The principal officer of the unit owner's association or such other officer as
                  is specifically designated shall furnish the above infor mation when requested to
                  do so in writing and within 30 days of the request.
                </Typography>
                <Typography variant="body2">
                  (c) Within 15 days of the recording of a mortgage or trust deed against a unit
                  ownership given by the owner of that unit to secure a debt, the owner shall inform
                  the Board of Managers of the unit owner's association of the identity of the
                  lender together with a mailing address at which the lender can receive notices
                  from the association. If a unit owner fails or refuses to inform the Board as
                  required un der subsection (c) then that unit owner shall be liable to the
                  association for all costs, expenses and reasonable attorneys fees and such other
                  damages, if any, incurred by the association as a result of such failure or
                  refusal.
                </Typography>
                <Typography variant="body2">
                  A reasonable fee covering the direct out-of-pocket cost of providing such
                  information and copying may be charged by the Association or its Board of Managers
                  to the unit seller for providing such information. (Source: PA 87-692.)
                </Typography>
              </Grid>
            </Grid>
          </DocumentContainer>
        ) : (
          <Grid container alignContent="center" justifyContent="center">
            <CircularProgress />
          </Grid>
        )}
      </FormContainer>
    </Layout>
  )
}
